import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiDownload, FiEdit2, FiSearch, FiX } from "react-icons/fi";
import * as XLSX from "xlsx";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import UnitVisibilityCheckbox, { saveUnitVisibility } from "../UnitVisibilityCheckbox";
import CorporatePicker from "../../../Pages/admin/CorporatePicker";
import UnitAliasDialog from "../popups/UnitAliasDialog";
import { buildSearchBlob } from "../itemsFamiliesUi";
import { getUnitMessages, UNIT_TYPES } from "../unitMessages";
import { getOfficialUnitName, unitSearchText } from "../../utils/unitNames";
import { usePurchasingSettingsSection } from "../usePurchasingSettingsSection";
import "../../PurchasingUi.css";
import "./UnitsTab.css";

export default function UnitsTab() {
  const { themeId, languageId } = usePmsTheme();
  const words = getUnitMessages(languageId);
  const { businessUnitId, canView: canViewUnits, canEdit, context, isCurrent, requestConfig } = usePurchasingSettingsSection("units");
  const generation = useRef(0);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [notice, setNotice] = useState(null);
  const [editingUnit, setEditingUnit] = useState(null);

  const fetchUnits = useCallback(async ({ quiet = false } = {}) => {
    const current = ++generation.current;
    if (!canViewUnits || !isCurrent()) {
      setUnits([]);
      setLoading(false);
      return false;
    }
    if (!quiet) setLoading(true);
    try {
      const response = await axios.get("/api/purchasing/units", {
        ...requestConfig, params: { settings: true, ...(businessUnitId ? { business_unit_id: businessUnitId } : {}) },
      });
      if (current !== generation.current || !isCurrent()) return false;
      setUnits(response.data || []);
      return true;
    } catch (requestError) {
      console.error("[UNITS][LOAD][ERROR]", requestError);
      if (current === generation.current && isCurrent()) setNotice({ tone: "error", key: "loadError" });
      return false;
    } finally {
      if (current === generation.current && !quiet) setLoading(false);
    }
  }, [businessUnitId, canViewUnits, isCurrent, requestConfig]);

  useEffect(() => {
    setUnits([]); setEditingUnit(null); setNotice(null);
    fetchUnits();
    return () => { generation.current += 1; };
  }, [context, fetchUnits]);

  const filteredUnits = useMemo(() => {
    const query = buildSearchBlob([search.trim()]);
    return units.filter((unit) => {
      if (typeFilter !== "all" && unit.type !== typeFilter) return false;
      return !query || buildSearchBlob([unitSearchText(unit), unit?.type, words.types[unit?.type]]).includes(query);
    });
  }, [search, typeFilter, units, words]);

  const hasFilters = search !== "" || typeFilter !== "all";
  const typeLabel = (type) => words.types[type] || type || "—";

  const exportToExcel = () => {
    if (!canViewUnits || !isCurrent()) return;
    if (!filteredUnits.length) {
      setNotice({ tone: "info", key: "exportEmpty" });
      return;
    }
    const rows = filteredUnits.map((unit) => ({
      [words.code]: unit?.code || "",
      [words.officialName]: getOfficialUnitName(unit, languageId),
      [words.hotelName]: unit.display_name || "",
      [words.type]: typeLabel(unit?.type),
      [words.visibility]: unit.is_hidden ? words.hidden : words.shown,
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, words.sheet);
    XLSX.writeFile(workbook, "units.xlsx");
    setNotice({ tone: "success", key: "exported", count: filteredUnits.length });
  };

  const saveAlias = async (displayName) => {
    if (!isCurrent("update")) return;
    await axios.put(`/api/purchasing/units/${editingUnit.id}/alias`, { display_name: displayName }, requestConfig);
    if (isCurrent("update")) await fetchUnits({ quiet: true });
  };
  const saveVisibility = async (unit, isHidden) => {
    if (!isCurrent("update")) return;
    const data = await saveUnitVisibility(`/api/purchasing/units/${unit.id}/visibility`, { is_hidden: isHidden }, requestConfig);
    if (isCurrent("update")) setUnits(rows => rows.map(row => row.id === unit.id ? { ...row, ...data } : row));
  };
  const noticeText = notice && (typeof words[notice.key] === "function" ? words[notice.key](notice) : words[notice.key]);

  return <>
    <section className="pilcore-purchasing-ui pu-units" data-theme={themeId} aria-labelledby="pu-units-title" aria-busy={loading}>
      <header className="pu-units__header">
        <div>
          <h2 id="pu-units-title">{words.title}</h2>
          <p>{words.description}</p>
        </div>
        {canViewUnits && <div className="pu-units__actions">
          <button type="button" className="pu-units__button" onClick={exportToExcel} disabled={loading}>
            <FiDownload aria-hidden="true" />{words.export}
          </button>
        </div>}
      </header>

      {!canViewUnits ? <p className="pu-units__empty" role="status">{words.noPermission}</p> : <>
        {!canEdit && <p role="status">{words.readOnly}</p>}
        <div className="pu-units__toolbar">
          <label className="pu-units__field pu-units__search">
            <span>{words.search}</span>
            <span className="pu-units__searchControl"><FiSearch aria-hidden="true" />
              <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={words.searchPlaceholder} />
            </span>
          </label>
          <div className="pu-units__field">
            <CorporatePicker label={words.type} value={typeFilter} onChange={value => setTypeFilter(value || "all")}
              options={[{ value: "all", label: words.allTypes }, ...UNIT_TYPES.map(type => ({ value: type, label: words.types[type] }))]} />
          </div>
          {hasFilters && <button type="button" className="pu-units__button pu-units__clear" onClick={() => {
            setSearch(""); setTypeFilter("all");
          }}><FiX aria-hidden="true" />{words.clearFilters}</button>}
        </div>

        {notice && <div className={`pu-units__notice pu-units__notice--${notice.tone}`} role={notice.tone === "error" ? "alert" : "status"}>
          <span>{noticeText}</span><button type="button" onClick={() => setNotice(null)} aria-label={words.dismiss}><FiX aria-hidden="true" /></button>
        </div>}

        <div className="pu-units__summary" aria-live="polite">
          <span>{words.visible}: <strong>{filteredUnits.length}</strong> / {units.length}</span>
        </div>
        <div className="pu-units__tableScroll" tabIndex={0} role="region" aria-label={words.table}>
          <table className="pu-units__table">
            <thead><tr><th scope="col">{words.code}</th><th scope="col">{words.officialName}</th><th scope="col">{words.hotelName}</th><th scope="col">{words.type}</th><th scope="col">{words.hide}</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={5} className="pu-units__empty" role="status">{words.loading}</td></tr> :
                !filteredUnits.length ? <tr><td colSpan={5} className="pu-units__empty">{words.empty}</td></tr> :
                  filteredUnits.map((unit) => <tr key={unit.id}>
                    <td><span className="pu-units__code">{unit?.code || `UNIT-${unit.id}`}</span></td>
                    <td className="pu-units__name">{getOfficialUnitName(unit, languageId)}</td>
                    <td><div className="pu-units__nameContent"><span>{unit.display_name || "—"}</span>
                      {canEdit && <button type="button" className="pu-units__button pu-units__edit" title={words.editAlias}
                        aria-label={`${words.editAlias}: ${getOfficialUnitName(unit, languageId)}`}
                        onClick={() => { if (isCurrent("update")) setEditingUnit(unit); }}><FiEdit2 aria-hidden="true" /></button>}
                    </div></td>
                    <td>{typeLabel(unit.type)}</td>
                    <td><UnitVisibilityCheckbox unit={unit} name={getOfficialUnitName(unit, languageId)} words={words}
                      disabled={!canEdit} onSave={hidden => saveVisibility(unit, hidden)} /></td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </>}
    </section>
    {editingUnit && canEdit && <UnitAliasDialog unit={editingUnit} onClose={() => setEditingUnit(null)} onSave={saveAlias} />}
  </>;
}
